import axios from "axios";

// Customer change requests (contract §H.2-§H.4, §I). Every action commits the
// whole envelope where it reads, and returns it, so a screen can keep its own
// copy when two lists of the same kind are on one page.
export default {
    namespaced: true,

    state: {
        change_requests: [],
        change_request: null,
        member_contacts: [],
        member_history: [],
    },

    getters: {
        changeRequests(state) {
            return state.change_requests;
        },
        changeRequest(state) {
            return state.change_request;
        },
        memberContacts(state) {
            return state.member_contacts;
        },
        memberHistory(state) {
            return state.member_history;
        },
    },

    mutations: {
        SET_CHANGE_REQUESTS(state, changeRequests) {
            return (state.change_requests = changeRequests);
        },
        // A fresh read (or a new draft). For the request already held, a read
        // older than it (a lower lock_version) is dropped.
        SET_CHANGE_REQUEST(state, changeRequest) {
            const current = state.change_request?.data;
            const incoming = changeRequest?.data;

            if (
                current &&
                incoming &&
                String(current.customer_change_request_id) === String(incoming.customer_change_request_id) &&
                Number(incoming.lock_version) < Number(current.lock_version)
            ) {
                return state.change_request;
            }

            return (state.change_request = changeRequest);
        },

        // The answer of a write on the held request. lock_version goes up on
        // every write (contract I.*), so an answer for another request, or
        // with a lower lock_version than the one held, is stale and ignored.
        UPDATE_CHANGE_REQUEST(state, changeRequest) {
            const current = state.change_request?.data;
            const incoming = changeRequest?.data;

            if (!incoming) {
                return state.change_request;
            }
            if (current) {
                if (String(current.customer_change_request_id) !== String(incoming.customer_change_request_id)) {
                    return state.change_request;
                }
                if (Number(incoming.lock_version) < Number(current.lock_version)) {
                    return state.change_request;
                }
            }

            return (state.change_request = changeRequest);
        },
        SET_MEMBER_CONTACTS(state, contacts) {
            return (state.member_contacts = contacts);
        },
        SET_MEMBER_HISTORY(state, history) {
            return (state.member_history = history);
        },
    },

    actions: {
        // GET /change-requests: filters {status, kind, customer_id, mine, page, per_page}
        async fetchChangeRequests({ commit }, filters) {
            return await axios
                .get("/api/v1/change-requests", { params: filters })
                .then((res) => {
                    const envelope = { ...res.data, data: res.data?.data ?? [] };
                    commit("SET_CHANGE_REQUESTS", envelope);
                    return envelope;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        async fetchChangeRequest({ commit }, id) {
            return await axios
                .get(`/api/v1/change-requests/${id}`)
                .then((res) => {
                    commit("SET_CHANGE_REQUEST", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // POST /customers/{id}/change-requests: the draft, with current_values
        async createChangeRequest({ commit }, { customerId, kind }) {
            return await axios
                .post(`/api/v1/customers/${customerId}/change-requests`, { kind: kind })
                .then((res) => {
                    commit("SET_CHANGE_REQUEST", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // POST /customers/{id}/change-requests with no body (doc 13 §3, S8):
        // creates the maker's member_update draft, or returns their open one.
        // 409 when another maker has an open request for this member.
        async createOrResume({ commit }, customerId) {
            return await axios
                .post(`/api/v1/customers/${customerId}/change-requests`)
                .then((res) => {
                    commit("SET_CHANGE_REQUEST", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // PATCH /change-requests/{id}: only the changed fields, and the fields to drop
        async updateChangeRequest({ commit }, { id, changes, remove }) {
            const payload = {};

            if (changes && Object.keys(changes).length) {
                payload.changes = changes;
            }

            if (remove && remove.length) {
                payload.remove = remove;
            }

            return await axios
                .patch(`/api/v1/change-requests/${id}`, payload)
                .then((res) => {
                    commit("UPDATE_CHANGE_REQUEST", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // PUT /change-requests/{id}/contacts: the whole proposed set
        async saveChangeRequestContacts({ commit }, { id, next_of_kin, referees }) {
            return await axios
                .put(`/api/v1/change-requests/${id}/contacts`, {
                    next_of_kin: next_of_kin || [],
                    referees: referees || [],
                })
                .then((res) => {
                    commit("UPDATE_CHANGE_REQUEST", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // POST /change-requests/{id}/files (doc 13 §4 "Files"): one file for one
        // type of the maker's own member_update request, multipart {file,
        // document_type_id}, optional Idempotency-Key header. 201, data = a file
        // block with status 'pending'. onProgress(0..100) is optional.
        async uploadChangeRequestFile({ commit }, { id, documentTypeId, file, idempotencyKey, onProgress }) {
            const form = new FormData();
            form.append("file", file);
            form.append("document_type_id", documentTypeId);

            const config = { showLoader: false, headers: {} };
            if (idempotencyKey) config.headers["Idempotency-Key"] = idempotencyKey;
            if (typeof onProgress === "function") {
                config.onUploadProgress = (event) => {
                    if (event && event.total) onProgress(Math.round((event.loaded * 100) / event.total));
                };
            }

            return await axios
                .post(`/api/v1/change-requests/${id}/files`, form, config)
                .then((res) => res.data)
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // PUT /change-requests/{id}/documents/{document_type_id}: a draft
        // version of one KYC document (doc 13 §4). payload {document_number,
        // issued_at, expires_at, file_ids?}. `file_ids` (the version's whole
        // ordered list) is sent only when the caller gives it: left out, a
        // draft keeps its files and a new version has none.
        async saveDocumentPart({ commit }, { id, documentTypeId, payload }) {
            const body = {
                document_number: payload?.document_number ?? null,
                issued_at: payload?.issued_at ?? null,
                expires_at: payload?.expires_at ?? null,
            };

            if (Array.isArray(payload?.file_ids)) {
                body.file_ids = payload.file_ids;
            }

            return await axios
                .put(`/api/v1/change-requests/${id}/documents/${documentTypeId}`, body)
                .then((res) => {
                    commit("UPDATE_CHANGE_REQUEST", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // DELETE /change-requests/{id}/documents/{document_type_id}: drops that draft version
        async removeDocumentPart({ commit }, { id, documentTypeId }) {
            return await axios
                .delete(`/api/v1/change-requests/${id}/documents/${documentTypeId}`)
                .then((res) => {
                    commit("UPDATE_CHANGE_REQUEST", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // PUT /change-requests/{id}/group-members: the whole proposed set,
        // members [{member_customer_id, office, is_signatory}]
        async saveGroupMembersPart({ commit }, { id, members }) {
            return await axios
                .put(`/api/v1/change-requests/${id}/group-members`, { members: members || [] })
                .then((res) => {
                    commit("UPDATE_CHANGE_REQUEST", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // POST /relationship-checks in the context of a change request (F.1):
        // {context_id, subject, phone?, phone_country?, id_no?}
        async checkRelationship({ commit }, payload) {
            return await axios
                .post("/api/v1/relationship-checks", { ...payload, context_type: "change_request" })
                .then((res) => res.data)
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // POST /change-requests/{id}/otp: a code to the new number
        async sendChangeRequestOtp({ commit }, { id, field }) {
            return await axios
                .post(`/api/v1/change-requests/${id}/otp`, { field: field })
                .then((res) => {
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        async verifyChangeRequestOtp({ commit }, { id, field, code }) {
            return await axios
                .post(`/api/v1/change-requests/${id}/otp/verify`, { field: field, code: code })
                .then((res) => {
                    commit("UPDATE_CHANGE_REQUEST", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // takes the id, or {id, reason, note} from the review of a member_update edit
        async submitChangeRequest({ commit }, request) {
            const isObject = request !== null && typeof request === "object";
            const id = isObject ? request.id : request;
            const payload = {};

            if (isObject && request.reason) {
                payload.reason = request.reason;
            }

            if (isObject && request.note) {
                payload.note = request.note;
            }

            return await axios
                .post(`/api/v1/change-requests/${id}/submit`, payload)
                .then((res) => {
                    commit("UPDATE_CHANGE_REQUEST", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // the maker may leave `reason` out; a checker must give one
        async cancelChangeRequest({ commit }, { id, reason }) {
            const payload = reason ? { reason: reason } : {};

            return await axios
                .post(`/api/v1/change-requests/${id}/cancel`, payload)
                .then((res) => {
                    commit("UPDATE_CHANGE_REQUEST", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // lock_version is the one of the request on screen, else the held one
        async approveChangeRequest({ commit, state }, { id, lock_version, flags_acknowledged, note }) {
            const held = state.change_request?.data;
            const heldVersion =
                held && String(held.customer_change_request_id) === String(id) ? held.lock_version : undefined;
            const payload = { lock_version: lock_version ?? heldVersion };

            if (flags_acknowledged !== undefined && flags_acknowledged !== null) {
                payload.flags_acknowledged = flags_acknowledged;
            }

            if (note) {
                payload.note = note;
            }

            return await axios
                .post(`/api/v1/change-requests/${id}/approve`, payload)
                .then((res) => {
                    commit("UPDATE_CHANGE_REQUEST", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        async rejectChangeRequest({ commit }, { id, reason }) {
            return await axios
                .post(`/api/v1/change-requests/${id}/reject`, { reason: reason })
                .then((res) => {
                    commit("UPDATE_CHANGE_REQUEST", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        async returnChangeRequest({ commit }, { id, reason }) {
            return await axios
                .post(`/api/v1/change-requests/${id}/return`, { reason: reason })
                .then((res) => {
                    commit("UPDATE_CHANGE_REQUEST", res.data);
                    return res.data;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // GET /customers/{id}/contacts: status current (default), superseded,
        // pending_review, rejected or all
        async fetchMemberContacts({ commit }, { customerId, status }) {
            const params = status ? { status: status } : {};

            return await axios
                .get(`/api/v1/customers/${customerId}/contacts`, { params: params })
                .then((res) => {
                    const envelope = { ...res.data, data: res.data?.data ?? [] };
                    commit("SET_MEMBER_CONTACTS", envelope);
                    return envelope;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // GET /customers/{id}/history/units: the member's history as units
        // (decided change requests, then the creation), newest first, paginated
        async fetchMemberHistoryUnits({ commit }, { customerId, page, per_page }) {
            const params = { page: page || 1 };

            if (per_page) {
                params.per_page = per_page;
            }

            return await axios
                .get(`/api/v1/customers/${customerId}/history/units`, { params: params })
                .then((res) => {
                    const envelope = { ...res.data, data: res.data?.data ?? [] };
                    commit("SET_MEMBER_HISTORY", envelope);
                    return envelope;
                })
                .catch((err) => {
                    return Promise.reject(err);
                });
        },

        // GET /customers/{id}/history/units/{changeRequestId}: one unit,
        // {change_request, decision, returns}
        async fetchMemberHistoryUnit(context, { customerId, changeRequestId }) {
            return await axios
                .get(`/api/v1/customers/${customerId}/history/units/${changeRequestId}`)
                .then((res) => res.data)
                .catch((err) => {
                    return Promise.reject(err);
                });
        },
    },
};
